import { useState, useMemo, Fragment } from "react";
import { Plus, Users, ChevronRight, ChevronDown, Percent } from "lucide-react";
import Button from "../ui/Button";
import MetricCard from "../ui/MetricCard";
import TeamForm from "./TeamForm";
import TeamProfile from "./TeamProfile";
import ContractorPaymentForm from "./ContractorPaymentForm";
import { formatUSD, formatPercent, formatMonths } from "../../lib/formatters";
import { monthsBetween } from "../../lib/dateUtils";
import { computePartnerOwedForMonth, computePartnerPaidForMonth, resolveTeamMonthSalary } from "../../lib/calculations";

const SUB_TABS = [
  { id: "employees", label: "Employees" },
  { id: "contractors", label: "Contractors" },
  { id: "partners", label: "Partners" },
];

function Header({ subTab, setSubTab, onAdd }) {
  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-brand-blue" />
          <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">Team & Payroll</h2>
        </div>
        <Button onClick={onAdd}>
          <Plus size={14} /> Add {subTab === "contractors" ? "contractor" : subTab === "partners" ? "partner" : "member"}
        </Button>
      </div>
      <div className="inline-flex rounded-lg border border-gray-200 dark:border-[var(--border-soft)] bg-white dark:bg-[var(--bg-surface)] p-0.5">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id
                ? "bg-brand-ink text-white"
                : "text-brand-slate hover:text-brand-ink dark:hover:text-[var(--text-primary)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </>
  );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthKeyFor = (year, monthIdx) => `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

// Was this employee active during the given month?
function activeIn(member, mKey) {
  if (!member?.startDate) return false;
  const monthStart = `${mKey}-01`;
  const [y, m] = mKey.split("-").map(Number);
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;
  const monthEndExclusive = `${nextY}-${String(nextM).padStart(2, "0")}-01`;
  if (member.startDate >= monthEndExclusive) return false;
  if (member.endDate && member.endDate < monthStart) return false;
  return true;
}

// Vacation-day count that falls inside (year, monthIdx).
function vacationDaysInMonth(vacations, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd = new Date(year, monthIdx + 1, 0);
  let days = 0;
  for (const v of vacations || []) {
    if (!v.startDate || !v.endDate) continue;
    const start = new Date(v.startDate + "T00:00:00");
    const end = new Date(v.endDate + "T00:00:00");
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const lo = start > monthStart ? start : monthStart;
    const hi = end < monthEnd ? end : monthEnd;
    if (hi < lo) continue;
    days += Math.round((hi - lo) / (1000 * 60 * 60 * 24)) + 1;
  }
  return days;
}

// Net cost for a single member in a single month = prorated salary
// (by start/end date + vacation) + bonus. Delegates to resolveTeamMonthSalary
// so partial first/last months pay the right amount (e.g. starts Jan 19 →
// only 13/31 of January's salary).
function memberMonthCost(member, mKey, allVacations, fxRates) {
  const planned = resolveTeamMonthSalary(member, mKey, allVacations, fxRates);
  const bonus = Number(member.bonusByMonth?.[mKey] || 0);
  return planned + bonus;
}

export default function Team({ state, dashboard, dateFilter }) {
  const { offers = [], settings = {} } = state;
  const allTeam = state.team || [];
  const team = useMemo(() => allTeam.filter((m) => m.type !== "contractor"), [allTeam]);
  const departments = settings.departments || [];

  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [subTab, setSubTab] = useState("employees");
  const [contractorsExpanded, setContractorsExpanded] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // IMPORTANT: every hook must be called on every render (rules of hooks).
  // No early returns above this block.
  const selectedMember = useMemo(
    () => allTeam.find((m) => m.id === selectedId) || null,
    [allTeam, selectedId]
  );

  const currentYear = new Date().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonth = dateFilter?.toMonth || 12;
  const currentMonthIdx = new Date().getMonth();
  const currentMonthKey = `${currentYear}-${String(currentMonthIdx + 1).padStart(2, "0")}`;

  const stats = useMemo(() => {
    const active = team.filter((m) => m.status === "active");
    const left = team.filter((m) => m.status === "left" && m.endDate);
    const monthlyPayroll = active.reduce((s, m) => s + (Number(m.monthlySalary) || 0), 0);
    const annualPayroll = monthlyPayroll * 12;
    const lifetimes = left.map((m) => monthsBetween(m.startDate, m.endDate)).filter((n) => n > 0);
    const avgLifetime = lifetimes.length ? lifetimes.reduce((a, b) => a + b, 0) / lifetimes.length : 0;
    const turnoverRate = team.length ? (left.length / team.length) * 100 : 0;
    return { activeCount: active.length, leftCount: left.length, monthlyPayroll, annualPayroll, avgLifetime, turnoverRate };
  }, [team]);

  const allVacations = state.vacations || [];
  const fxRates = state.fxRates || [];
  // Employee IDs that are partners — keep them visible even if salary is $0.
  const partnerEmployeeIds = useMemo(() => {
    const allPartnerIds = new Set((state.partnerRules || []).map((r) => r.partnerId));
    return new Set(team.filter((m) => allPartnerIds.has(m.id)).map((m) => m.id));
  }, [team, state.partnerRules]);

  const rows = useMemo(() => {
    return team
      .map((m) => {
        const months = new Array(12).fill(0);
        for (let i = 0; i < 12; i++) months[i] = memberMonthCost(m, monthKeyFor(year, i), allVacations, fxRates);
        const yearTotal = months.reduce((a, b) => a + b, 0);
        return { member: m, months, yearTotal };
      })
      // Show member if they had payroll cost (any active or historical
      // salary) OR they're currently active and NOT a pure partner. A pure
      // partner (rule attached, $0 salary) is hidden here — they live in the
      // Partners sub-tab instead, so they don't clutter the payroll view.
      .filter((r) => {
        if (r.yearTotal > 0) return true;
        if (partnerEmployeeIds.has(r.member.id)) return false;
        return r.member.status === "active";
      })
      .sort((a, b) => b.yearTotal - a.yearTotal);
  }, [team, year, allVacations, partnerEmployeeIds]);

  const employeeMonthlyTotals = useMemo(
    () => MONTH_LABELS.map((_, i) => rows.reduce((s, r) => s + (r.months[i] || 0), 0)),
    [rows]
  );
  const employeeYearTotal = employeeMonthlyTotals.reduce((a, b) => a + b, 0);

  // ────────── Contractor rollups (collapsible row + dedicated sub-tab) ──────
  const contractors = useMemo(() => allTeam.filter((m) => m.type === "contractor"), [allTeam]);
  const contractorPayments = state.contractorPayments || [];
  const oneOffPayments = state.oneOffPayments || [];
  const partnerRules = state.partnerRules || [];
  // Set of contractor IDs that are partners (have at least one rule). Used so
  // a profit-share partner with $0 of payments still shows up as a row — his
  // partner cut accrues on the Departments / Dashboard views regardless.
  const partnerContractorIds = useMemo(() => {
    const partnerIds = new Set(partnerRules.map((r) => r.partnerId));
    return new Set(contractors.filter((c) => partnerIds.has(c.id)).map((c) => c.id));
  }, [contractors, partnerRules]);
  const contractorRows = useMemo(() => {
    const map = new Map(); // contractorId -> Array<number, 12>
    // Source 1: native contractor_payments (logged via "Log payment" form).
    for (const p of contractorPayments) {
      if (!p.date || !p.contractorId) continue;
      const y = Number(p.date.slice(0, 4));
      if (y !== year) continue;
      const idx = Number(p.date.slice(5, 7)) - 1;
      if (idx < 0 || idx > 11) continue;
      const row = map.get(p.contractorId) || new Array(12).fill(0);
      row[idx] += Number(p.usdAmount) || 0;
      map.set(p.contractorId, row);
    }
    // Source 2: bank-feed expenses (Expenses tab) matched to a contractor.
    // Refunds (direction "in") subtract so the math mirrors tools.
    for (const p of oneOffPayments) {
      if (p.matchedSourceType !== "contractor" || !p.matchedSourceId) continue;
      if (!p.date) continue;
      const y = Number(p.date.slice(0, 4));
      if (y !== year) continue;
      const idx = Number(p.date.slice(5, 7)) - 1;
      if (idx < 0 || idx > 11) continue;
      const sign = (p.direction || "out") === "out" ? 1 : -1;
      const row = map.get(p.matchedSourceId) || new Array(12).fill(0);
      row[idx] += sign * (Number(p.amount) || 0);
      map.set(p.matchedSourceId, row);
    }
    // Source 3: partner payouts (Expenses tagged matchedSourceType='partner_payout').
    // These are real cash flowing to partner-contractors; surface them here too.
    for (const p of oneOffPayments) {
      if (p.matchedSourceType !== "partner_payout" || !p.matchedSourceId) continue;
      if (!p.date) continue;
      const y = Number(p.date.slice(0, 4));
      if (y !== year) continue;
      const idx = Number(p.date.slice(5, 7)) - 1;
      if (idx < 0 || idx > 11) continue;
      const sign = (p.direction || "out") === "out" ? 1 : -1;
      const row = map.get(p.matchedSourceId) || new Array(12).fill(0);
      row[idx] += sign * (Number(p.amount) || 0);
      map.set(p.matchedSourceId, row);
    }
    const out = [];
    for (const c of contractors) {
      const months = map.get(c.id) || new Array(12).fill(0);
      const yearTotal = months.reduce((a, b) => a + b, 0);
      // Visibility rules for the Contractors view:
      //   1. Always show if they had real contractor activity this year.
      //   2. Hide pure partners (partner rules + no contractor activity) — they
      //      already have a dedicated Partners sub-tab and would just clutter
      //      this view with empty rows.
      //   3. Otherwise show only if currently active (gives a new active
      //      contractor a row to populate).
      if (yearTotal > 0) {
        out.push({ contractor: c, months, yearTotal });
        continue;
      }
      if (partnerContractorIds.has(c.id)) continue; // pure partner → skip
      if (c.status !== "active") continue;          // historical-only → skip
      out.push({ contractor: c, months, yearTotal });
    }
    out.sort((a, b) => b.yearTotal - a.yearTotal);
    return out;
  }, [contractors, contractorPayments, oneOffPayments, partnerContractorIds, year]);

  const contractorMonthlyTotals = useMemo(
    () => MONTH_LABELS.map((_, i) => contractorRows.reduce((s, r) => s + (r.months[i] || 0), 0)),
    [contractorRows]
  );
  const contractorYearTotal = contractorMonthlyTotals.reduce((a, b) => a + b, 0);

  const grandMonthlyTotals = employeeMonthlyTotals.map((v, i) => v + contractorMonthlyTotals[i]);
  const grandTotal = employeeYearTotal + contractorYearTotal;

  // ────────── Partner rollups (Partners sub-tab) ──────────────────────────
  // One row per partner (team member with at least one rule). Each row carries
  // 12 monthly OWED amounts (computed from rules + dept/agency/offer P&L),
  // plus YTD owed/paid/outstanding totals.
  const partnerRows = useMemo(() => {
    const partnerIds = [...new Set((state.partnerRules || []).map((r) => r.partnerId))];
    const fxRates = state.fxRates || [];
    const out = [];
    for (const pid of partnerIds) {
      const partner = allTeam.find((m) => m.id === pid);
      if (!partner) continue;
      const salaryMonths = new Array(12).fill(0);
      const owedMonths = new Array(12).fill(0);
      const paidMonths = new Array(12).fill(0);
      for (let i = 0; i < 12; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        salaryMonths[i] = resolveTeamMonthSalary(partner, mKey, allVacations, fxRates);
        owedMonths[i] = computePartnerOwedForMonth(pid, mKey, state).owed;
        paidMonths[i] = computePartnerPaidForMonth(pid, mKey, state);
      }
      const salaryYtd = salaryMonths.reduce((a, b) => a + b, 0);
      const owedYtd = owedMonths.reduce((a, b) => a + b, 0);
      const paidYtd = paidMonths.reduce((a, b) => a + b, 0);
      out.push({
        partner,
        salaryMonths, owedMonths, paidMonths,
        salaryYtd, owedYtd, paidYtd,
        outstanding: owedYtd - paidYtd,
        totalCompYtd: salaryYtd + owedYtd, // total earned (salary + dividend accrued)
      });
    }
    out.sort((a, b) => b.totalCompYtd - a.totalCompYtd);
    return out;
  }, [allTeam, state, year, allVacations]);

  const partnerSalaryMonthlyTotals = useMemo(
    () => MONTH_LABELS.map((_, i) => partnerRows.reduce((s, r) => s + (r.salaryMonths[i] || 0), 0)),
    [partnerRows]
  );
  const partnerOwedMonthlyTotals = useMemo(
    () => MONTH_LABELS.map((_, i) => partnerRows.reduce((s, r) => s + (r.owedMonths[i] || 0), 0)),
    [partnerRows]
  );
  const partnerSalaryYearTotal = partnerSalaryMonthlyTotals.reduce((a, b) => a + b, 0);
  const partnerOwedYearTotal = partnerOwedMonthlyTotals.reduce((a, b) => a + b, 0);
  const partnerPaidYearTotal = partnerRows.reduce((s, r) => s + r.paidYtd, 0);

  // ────────── Conditional render branches (all hooks above this line) ──────
  if (selectedMember) {
    return (
      <TeamProfile
        member={selectedMember}
        state={state}
        dashboard={dashboard}
        dateFilter={dateFilter}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  if (subTab === "contractors") {
    return (
      <div className="space-y-5">
        <Header subTab={subTab} setSubTab={setSubTab} onAdd={() => setShowAdd(true)} />
        <ContractorMatrix
          contractors={contractors}
          rows={contractorRows}
          monthlyTotals={contractorMonthlyTotals}
          yearTotal={contractorYearTotal}
          year={year}
          fromMonth={fromMonth}
          toMonth={toMonth}
          currentYear={currentYear}
          currentMonthIdx={currentMonthIdx}
          onSelect={(id) => setSelectedId(id)}
          onLogPayment={() => setPaymentOpen(true)}
        />
        <TeamForm
          open={showAdd}
          onClose={() => setShowAdd(false)}
          onSubmit={(data) => dashboard.addTeamMember({ ...data, type: "contractor" })}
          offers={offers}
          fxRates={state.fxRates || []}
          departments={departments}
        />
        {paymentOpen && (
          <ContractorPaymentForm
            open={true}
            onClose={() => setPaymentOpen(false)}
            onSubmit={(data) => dashboard.addContractorPayment(data)}
            contractors={contractors}
            fxRates={state.fxRates || []}
          />
        )}
      </div>
    );
  }

  if (subTab === "partners") {
    return (
      <div className="space-y-5">
        <Header subTab={subTab} setSubTab={setSubTab} onAdd={() => setShowAdd(true)} />
        <PartnerMatrix
          rows={partnerRows}
          salaryMonthlyTotals={partnerSalaryMonthlyTotals}
          owedMonthlyTotals={partnerOwedMonthlyTotals}
          salaryYearTotal={partnerSalaryYearTotal}
          owedYearTotal={partnerOwedYearTotal}
          paidYearTotal={partnerPaidYearTotal}
          year={year}
          fromMonth={fromMonth}
          toMonth={toMonth}
          currentYear={currentYear}
          currentMonthIdx={currentMonthIdx}
          onSelect={(id) => setSelectedId(id)}
        />
        {/* Adding from Partners tab: create as contractor with $0 salary by
            default, then immediately open the new member's profile so the
            user lands on the "Partner rules" editor without an extra click. */}
        <TeamForm
          open={showAdd}
          onClose={() => setShowAdd(false)}
          onSubmit={(data) => {
            const created = dashboard.addTeamMember({ ...data, type: data.type || "contractor" });
            if (created?.id) setSelectedId(created.id);
          }}
          offers={offers}
          fxRates={state.fxRates || []}
          departments={departments}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Header subTab={subTab} setSubTab={setSubTab} onAdd={() => setShowAdd(true)} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label="Active" value={stats.activeCount} />
        <MetricCard label="Left" value={stats.leftCount} />
        <MetricCard label="Monthly payroll" value={formatUSD(stats.monthlyPayroll)} accent="orange" />
        <MetricCard label="Annual payroll" value={formatUSD(stats.annualPayroll)} accent="orange" />
        <MetricCard label="Avg tenure (left)" value={formatMonths(stats.avgLifetime)} />
        <MetricCard label="Turnover" value={formatPercent(stats.turnoverRate)} accent={stats.turnoverRate > 30 ? "red" : "default"} />
      </div>

      {/* Matrix */}
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
          <div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
              Team payroll · {year}
            </div>
            <div className="text-xs text-brand-slate mt-0.5">
              Salary + bonus per month. Click a name to open profile and edit vacations / bonuses.
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Year total</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(employeeYearTotal)}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col style={{ width: "20%" }} />
              {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${72 / 12}%` }} />)}
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Member</th>
                {MONTH_LABELS.map((m, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <th key={m} className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`}>
                      {m}
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ member, months, yearTotal }) => (
                <tr key={member.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
                  <td
                    className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue"
                    onClick={() => setSelectedId(member.id)}
                    title="Open team member profile"
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-brand-ink text-white text-[10px] font-display font-semibold flex items-center justify-center shrink-0">
                        {(member.name || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        <span className="block">{member.name}</span>
                        {member.role && <span className="block text-[10px] text-brand-slate">{member.role}</span>}
                      </span>
                    </span>
                  </td>
                  {months.map((amount, i) => {
                    const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                    const isCurrent = i === currentMonthIdx && year === currentYear;
                    return (
                      <td
                        key={i}
                        className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${!inRange ? "text-gray-300" : amount > 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-gray-300"}`}
                      >
                        {amount > 0 ? formatUSD(amount) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                    {formatUSD(yearTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50/60 dark:bg-[var(--bg-soft)]/60">
                <td className="px-3 py-2 font-semibold text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                  Employees subtotal
                </td>
                {employeeMonthlyTotals.map((amount, i) => {
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <td
                      key={i}
                      className={`px-1 py-2 text-center font-semibold ${isCurrent ? "bg-brand-blue-soft/30 text-brand-blue-dark" : "text-brand-ink dark:text-[var(--text-primary)]"}`}
                    >
                      {amount > 0 ? formatUSD(amount) : "—"}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                  {formatUSD(employeeYearTotal)}
                </td>
              </tr>

              {/* Collapsible Contractors summary row */}
              <tr
                className={`border-t border-gray-100 dark:border-[var(--border-soft)] bg-amber-50/40 dark:bg-amber-900/10 ${contractorRows.length > 0 ? "cursor-pointer hover:bg-amber-100/60 dark:hover:bg-amber-900/20" : ""}`}
                onClick={contractorRows.length > 0 ? () => setContractorsExpanded((v) => !v) : undefined}
                title={contractorRows.length > 0 ? (contractorsExpanded ? "Collapse contractors" : "Expand contractors") : undefined}
              >
                <td className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">
                  <span className="inline-flex items-center gap-1.5">
                    {contractorRows.length > 0 && (
                      contractorsExpanded
                        ? <ChevronDown size={12} className="text-brand-slate" />
                        : <ChevronRight size={12} className="text-brand-slate" />
                    )}
                    Contractors
                  </span>
                </td>
                {contractorMonthlyTotals.map((amount, i) => {
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <td
                      key={i}
                      className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${amount > 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-gray-300"}`}
                    >
                      {amount > 0 ? formatUSD(amount) : "—"}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                  {contractorYearTotal > 0 ? formatUSD(contractorYearTotal) : "—"}
                </td>
              </tr>

              {/* Expanded per-contractor rows */}
              {contractorsExpanded && contractorRows.map(({ contractor, months, yearTotal }) => (
                <tr key={contractor.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] bg-amber-50/20 dark:bg-amber-900/5">
                  <td
                    className="pl-8 pr-3 py-2 text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue text-xs"
                    onClick={() => setSelectedId(contractor.id)}
                    title="Open contractor profile"
                  >
                    {contractor.name}
                  </td>
                  {months.map((amount, i) => {
                    const isCurrent = i === currentMonthIdx && year === currentYear;
                    return (
                      <td
                        key={i}
                        className={`px-1 py-2 text-center text-xs ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${amount > 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-gray-300"}`}
                      >
                        {amount > 0 ? formatUSD(amount) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right text-xs font-medium text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                    {formatUSD(yearTotal)}
                  </td>
                </tr>
              ))}

              {/* Grand total = employees + contractors */}
              <tr className="border-t-2 border-gray-300 dark:border-[var(--border-soft)] bg-brand-ink text-white">
                <td className="px-3 py-2 font-bold border-r border-white/10">Sub team total</td>
                {grandMonthlyTotals.map((amount, i) => {
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <td key={i} className={`px-1 py-2 text-center font-bold ${isCurrent ? "text-brand-mint" : "text-white"}`}>
                      {amount > 0 ? formatUSD(amount) : "—"}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-bold text-white border-l border-white/10">
                  {formatUSD(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <TeamForm
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={(data) => dashboard.addTeamMember(data)}
        offers={offers}
        fxRates={state.fxRates || []}
        departments={departments}
      />
    </div>
  );
}

// Contractor matrix — same shape as the Employees matrix, drives the
// Contractors sub-tab.
function ContractorMatrix({ contractors, rows, monthlyTotals, yearTotal, year, fromMonth, toMonth, currentYear, currentMonthIdx, onSelect, onLogPayment }) {
  if (contractors.length === 0) {
    return (
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-8 text-center">
        <Users size={28} className="text-brand-slate mx-auto mb-3" />
        <div className="font-medium text-brand-ink dark:text-[var(--text-primary)]">No contractors yet</div>
        <div className="text-sm text-brand-slate mt-1">Click "Add contractor" to add one.</div>
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Contractor payments · {year}</div>
          <div className="text-xs text-brand-slate mt-0.5">Sum of actual payments per contractor, per month. Click a name to open their profile.</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Year total</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(yearTotal)}</div>
          </div>
          {onLogPayment && (
            <button onClick={onLogPayment} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-blue text-white hover:bg-brand-blue-dark">
              <Plus size={13} /> Log payment
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: "20%" }} />
            {MONTH_LABELS.map((_, i) => <col key={i} style={{ width: `${72 / 12}%` }} />)}
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]">Contractor</th>
              {MONTH_LABELS.map((m, i) => {
                const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <th key={m} className={`px-1 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`}>{m}</th>
                );
              })}
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ contractor, months, yearTotal: rowTotal }) => (
              <tr key={contractor.id} className="border-t border-gray-100 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
                <td
                  className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue"
                  onClick={() => onSelect?.(contractor.id)}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-brand-ink text-white text-[10px] font-display font-semibold flex items-center justify-center shrink-0">
                      {(contractor.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <span>
                      <span className="block">{contractor.name}</span>
                      {contractor.role && <span className="block text-[10px] text-brand-slate">{contractor.role}</span>}
                    </span>
                  </span>
                </td>
                {months.map((amount, i) => {
                  const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                  const isCurrent = i === currentMonthIdx && year === currentYear;
                  return (
                    <td key={i} className={`px-1 py-2 text-center ${isCurrent ? "bg-brand-blue-soft/30 font-medium" : ""} ${!inRange ? "text-gray-300" : amount > 0 ? "text-brand-ink dark:text-[var(--text-primary)]" : "text-gray-300"}`}>
                      {amount > 0 ? formatUSD(amount) : "—"}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                  {formatUSD(rowTotal)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50/60 dark:bg-[var(--bg-soft)]/60">
              <td className="px-3 py-2 font-semibold text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]">Contractors subtotal</td>
              {monthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <td key={i} className={`px-1 py-2 text-center font-semibold ${isCurrent ? "bg-brand-blue-soft/30 text-brand-blue-dark" : "text-brand-ink dark:text-[var(--text-primary)]"}`}>
                    {amount > 0 ? formatUSD(amount) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)]">
                {formatUSD(yearTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}


// Partner matrix — same layout as ContractorMatrix, but the cell value is the
// monthly cut OWED (computed from partner rules × dept/agency/offer P&L).
// Trailing columns: Owed YTD / Paid YTD / Outstanding. Click a name to open
// the team profile and edit rules.
function PartnerMatrix({ rows, salaryMonthlyTotals, owedMonthlyTotals, salaryYearTotal, owedYearTotal, paidYearTotal, year, fromMonth, toMonth, currentYear, currentMonthIdx, onSelect }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card p-8 text-center">
        <Percent size={28} className="text-brand-slate mx-auto mb-3" />
        <div className="font-medium text-brand-ink dark:text-[var(--text-primary)]">No partners configured</div>
        <div className="text-sm text-brand-slate mt-1 max-w-md mx-auto">
          A partner is anyone who gets a % cut of revenue or net profit (e.g. Waleed = 50% of SEO, Karim = 25% of agency net profit on top of his salary). Click <strong>Add partner</strong> above — it creates a $0-salary contractor and drops you straight into their profile to set the rule. Or, for an existing employee like Karim, open his profile and use the "Partner rules" section.
        </div>
      </div>
    );
  }
  const outstandingTotal = owedYearTotal - paidYearTotal;
  const totalCompYearTotal = salaryYearTotal + owedYearTotal;
  // Combined per-month totals = salary + dividend for the footer "Total" row.
  const combinedMonthlyTotals = MONTH_LABELS.map((_, i) => (salaryMonthlyTotals[i] || 0) + (owedMonthlyTotals[i] || 0));
  return (
    <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Partner compensation · {year}</div>
          <div className="text-xs text-brand-slate mt-0.5">
            Two rows per partner: <strong>Salary</strong> (fixed) and <strong>Dividend</strong> (% cut). Click a name to edit rules.
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Salary YTD</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(salaryYearTotal)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Dividend owed YTD</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(owedYearTotal)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Dividend paid YTD</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(paidYearTotal)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-brand-slate">Total comp YTD</div>
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(totalCompYearTotal)}</div>
          </div>
        </div>
      </div>
      {/* Horizontal-scroll wrapper so wide tables don't crush the cells. The
          inner table has a min-width that keeps every number readable. */}
      <div className="overflow-x-auto matrix-scroll">
        <table className="w-full text-xs" style={{ minWidth: "1400px" }}>
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]" style={{ width: 160 }}>Partner</th>
              <th className="text-left px-2 py-2 font-medium text-brand-slate uppercase tracking-wide text-[10px] border-r border-gray-100 dark:border-[var(--border-soft)]" style={{ width: 90 }}>Line</th>
              {MONTH_LABELS.map((m, i) => {
                const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <th key={m} className={`px-2 py-2 text-center font-medium uppercase tracking-wide text-[10px] whitespace-nowrap ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : inRange ? "text-brand-slate" : "text-gray-300"}`} style={{ minWidth: 78 }}>{m}</th>
                );
              })}
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px] border-l border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap" style={{ minWidth: 90 }}>YTD</th>
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px] whitespace-nowrap" style={{ minWidth: 80 }}>Paid</th>
              <th className="px-2 py-2 text-right font-medium text-brand-slate uppercase tracking-wide text-[10px] whitespace-nowrap" style={{ minWidth: 100 }}>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ partner, salaryMonths, owedMonths, salaryYtd, owedYtd, paidYtd, outstanding }, partnerIdx) => {
              // Combined per-month total for THIS partner = salary + dividend.
              const totalMonths = salaryMonths.map((s, i) => (s || 0) + (owedMonths[i] || 0));
              const totalYtd = salaryYtd + owedYtd;
              const cellClass = (amount, i, type = "default") => {
                const inRange = i >= fromMonth - 1 && i <= toMonth - 1;
                const isCurrent = i === currentMonthIdx && year === currentYear;
                const baseColor = !inRange
                  ? "text-gray-300"
                  : amount > 0
                  ? (type === "total" ? "text-brand-ink dark:text-[var(--text-primary)] font-semibold" : "text-brand-ink dark:text-[var(--text-primary)]")
                  : "text-gray-300";
                return `px-2 py-2 text-center whitespace-nowrap ${isCurrent ? "bg-brand-blue-soft/30" : ""} ${baseColor}`;
              };
              return (
                <Fragment key={partner.id}>
                  {/* Salary row — owns the partner-name cell (rowSpan=3) */}
                  <tr className={`${partnerIdx === 0 ? "border-t" : "border-t-2"} border-gray-200 dark:border-[var(--border-soft)] hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]`}>
                    <td
                      className="px-3 py-2 font-medium text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] cursor-pointer hover:text-brand-blue align-top"
                      onClick={() => onSelect?.(partner.id)}
                      title="Open profile to edit rules"
                      rowSpan={3}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-brand-ink text-white text-[10px] font-display font-semibold flex items-center justify-center shrink-0">
                          {(partner.name || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span>
                          <span className="block">{partner.name}</span>
                          {partner.role && <span className="block text-[10px] text-brand-slate">{partner.role}</span>}
                        </span>
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[10px] uppercase tracking-wide text-brand-slate border-r border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">Salary</td>
                    {salaryMonths.map((amount, i) => (
                      <td key={i} className={cellClass(amount, i)}>{amount > 0.5 ? formatUSD(amount) : "—"}</td>
                    ))}
                    <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">{formatUSD(salaryYtd)}</td>
                    <td className="px-2 py-2 text-right text-brand-slate whitespace-nowrap">—</td>
                    <td className="px-2 py-2 text-right text-brand-slate whitespace-nowrap">—</td>
                  </tr>
                  {/* Dividend row */}
                  <tr className="bg-purple-50/40 dark:bg-purple-900/10 hover:bg-purple-50/70 dark:hover:bg-purple-900/20">
                    <td className="px-2 py-2 text-[10px] uppercase tracking-wide text-purple-700 dark:text-purple-300 font-semibold border-r border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">Dividend</td>
                    {owedMonths.map((amount, i) => (
                      <td key={i} className={cellClass(amount, i)}>{amount > 0.5 ? formatUSD(amount) : "—"}</td>
                    ))}
                    <td className="px-2 py-2 text-right font-semibold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">{formatUSD(owedYtd)}</td>
                    <td className="px-2 py-2 text-right text-brand-slate whitespace-nowrap">{formatUSD(paidYtd)}</td>
                    <td className={`px-2 py-2 text-right font-bold whitespace-nowrap ${
                      outstanding > 0.5 ? "text-amber-700 dark:text-amber-300" :
                      outstanding < -0.5 ? "text-red-600 dark:text-red-400" :
                      "text-emerald-700 dark:text-emerald-300"
                    }`}>
                      {outstanding > 0.5 ? formatUSD(outstanding) : outstanding < -0.5 ? `−${formatUSD(Math.abs(outstanding))}` : formatUSD(0)}
                    </td>
                  </tr>
                  {/* Total row — per-month sum (salary + dividend) for this partner */}
                  <tr className="bg-gray-100/70 dark:bg-[var(--bg-soft)]/80 font-medium">
                    <td className="px-2 py-2 text-[10px] uppercase tracking-wide font-bold text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">Total</td>
                    {totalMonths.map((amount, i) => (
                      <td key={i} className={cellClass(amount, i, "total")}>{amount > 0.5 ? formatUSD(amount) : "—"}</td>
                    ))}
                    <td className="px-2 py-2 text-right font-bold text-brand-ink dark:text-[var(--text-primary)] border-l border-gray-100 dark:border-[var(--border-soft)] whitespace-nowrap">{formatUSD(totalYtd)}</td>
                    <td className="px-2 py-2 text-right text-brand-slate whitespace-nowrap">—</td>
                    <td className="px-2 py-2 text-right text-brand-slate whitespace-nowrap">—</td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 dark:border-[var(--border-soft)] bg-brand-ink text-white">
              <td colSpan={2} className="px-3 py-2 font-bold border-r border-white/10 whitespace-nowrap">All partners total</td>
              {combinedMonthlyTotals.map((amount, i) => {
                const isCurrent = i === currentMonthIdx && year === currentYear;
                return (
                  <td key={i} className={`px-2 py-2 text-center font-bold whitespace-nowrap ${isCurrent ? "text-brand-mint" : "text-white"}`}>
                    {amount > 0.5 ? formatUSD(amount) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right font-bold border-l border-white/10 whitespace-nowrap">{formatUSD(totalCompYearTotal)}</td>
              <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">{formatUSD(paidYearTotal)}</td>
              <td className={`px-2 py-2 text-right font-bold whitespace-nowrap ${
                outstandingTotal > 0.5 ? "text-amber-300" :
                outstandingTotal < -0.5 ? "text-red-300" :
                "text-brand-mint"
              }`}>{formatUSD(outstandingTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
